/**
 * Custom hook for managing PDF scroll mode
 *
 * Extracted from PDFViewerModal.tsx
 */

import { useState, useEffect, useCallback } from 'react';
import type { ScrollMode } from '../types/pdf-viewer.types';

export interface UsePDFScrollModeProps {
  containerRef: React.RefObject<HTMLDivElement>;
  isOpen: boolean;
  goToPreviousPage: () => void;
  goToNextPage: () => void;
}

export interface UsePDFScrollModeReturn {
  scrollMode: ScrollMode;
  setScrollMode: (mode: ScrollMode) => void;
}

/**
 * Manages PDF scroll mode (continuous vs page-by-page)
 */
export function usePDFScrollMode({
  containerRef,
  isOpen,
  goToPreviousPage,
  goToNextPage,
}: UsePDFScrollModeProps): UsePDFScrollModeReturn {
  const [scrollMode, setScrollModeState] = useState<ScrollMode>('continuous');

  // Load from localStorage
  useEffect(() => {
    try {
      const savedScrollMode = localStorage.getItem('pdf-viewer-scroll-mode');
      if (savedScrollMode === 'page-by-page' || savedScrollMode === 'continuous') {
        setScrollModeState(savedScrollMode);
      }
    } catch (error) {
      console.error('Error loading scroll mode:', error);
    }
  }, []);

  // Save to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('pdf-viewer-scroll-mode', scrollMode);
    } catch (error) {
      console.error('Error saving scroll mode:', error);
    }
  }, [scrollMode]);

  // Handle wheel events for page-by-page mode
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isOpen) return;

    const handleWheel = (e: WheelEvent) => {
      // Ctrl + Scroll = Zoom (always works)
      if (e.ctrlKey || e.metaKey) {
        return; // Let zoom handler deal with this
      }

      // Page-by-page scroll mode
      if (scrollMode === 'page-by-page') {
        e.preventDefault();

        if (e.deltaY < 0) {
          // Scroll up = Previous page
          goToPreviousPage();
        } else {
          // Scroll down = Next page
          goToNextPage();
        }
      }
      // Continuous mode: let it flow naturally
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [isOpen, scrollMode, containerRef, goToPreviousPage, goToNextPage]);

  const setScrollMode = useCallback((mode: ScrollMode) => {
    setScrollModeState(mode);
  }, []);

  return {
    scrollMode,
    setScrollMode,
  };
}
